import { useRouter, useSearchParams } from 'next/navigation';
import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';

import { Room, SpaceHistory, AppState } from '@/types/room';

// アクションの型定義
type AppAction =
  | { type: 'SET_ROOMS'; payload: Room[] }
  | { type: 'SET_CURRENT_ROOM'; payload: string }
  | { type: 'SET_CURRENT_HISTORY'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SYNC_ENABLED'; payload: boolean }
  | { type: 'FORCE_SYNC_ENABLED' }
  | { type: 'SET_ROOM_LIST_VISIBLE'; payload: boolean }
  | { type: 'SET_HISTORY_SLIDER_CHANGE'; payload: boolean }
  | { type: 'SHOW_TOAST'; payload: string }
  | { type: 'HIDE_TOAST' }
  | { type: 'SET_LOD_SPLAT_SCALE'; payload: number }
  | {
      type: 'SET_POINT_CLOUD_METRICS';
      payload: { originalSplats: number; estimatedActiveSplats: number; isLoaded: boolean };
    }
  | { type: 'SET_POINTER_ENABLED'; payload: boolean }
  | { type: 'SET_RAD_LOADING'; payload: boolean }
  | { type: 'TOGGLE_UI_HIDDEN' };

// 初期状態
const initialState: AppState = {
  rooms: [],
  currentRoomId: '',
  currentHistoryId: '',
  isLoading: false,
  isSyncEnabled: false, // デフォルトでは同期機能を無効に
  isRoomListVisible: false, // デフォルトではルームリストは非表示
  isHistorySliderChange: false, // デフォルトではhistory-sliderによる変更ではない
  toastMessage: '',
  isToastVisible: false,
  lodSplatScale: 1.0,
  pointCloudMetrics: {
    originalSplats: 0,
    estimatedActiveSplats: 0,
    isLoaded: false
  },
  isPointerEnabled: true,
  isRadLoading: false,
  isUiHidden: false
};

// リデューサー
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ROOMS':
      return { ...state, rooms: action.payload };
    case 'SET_CURRENT_ROOM':
      return { ...state, currentRoomId: action.payload };
    case 'SET_CURRENT_HISTORY':
      return { ...state, currentHistoryId: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SYNC_ENABLED':
      return { ...state, isSyncEnabled: action.payload };
    case 'FORCE_SYNC_ENABLED':
      // 強制同期: 常にtrueに設定
      return { ...state, isSyncEnabled: true };
    case 'SET_ROOM_LIST_VISIBLE':
      return { ...state, isRoomListVisible: action.payload };
    case 'SET_HISTORY_SLIDER_CHANGE':
      return { ...state, isHistorySliderChange: action.payload };
    case 'SHOW_TOAST':
      return { ...state, toastMessage: action.payload, isToastVisible: true };
    case 'HIDE_TOAST':
      return { ...state, isToastVisible: false };
    case 'SET_LOD_SPLAT_SCALE':
      return { ...state, lodSplatScale: action.payload };
    case 'SET_POINT_CLOUD_METRICS':
      return { ...state, pointCloudMetrics: action.payload };
    case 'SET_POINTER_ENABLED':
      return { ...state, isPointerEnabled: action.payload };
    case 'SET_RAD_LOADING':
      return { ...state, isRadLoading: action.payload };
    case 'TOGGLE_UI_HIDDEN':
      return { ...state, isUiHidden: !state.isUiHidden };
    default:
      return state;
  }
}

// コンテキストの型定義
type AppContextType = {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  switchRoom: (roomId: string) => void;
  switchHistory: (historyId: string) => void;
  toggleSync: () => void;
  forceSyncEnabled: () => void;
  togglePointer: () => void;
  toggleUiHidden: () => void;
  showToast: (message: string) => void;
  hideToast: () => void;
  getCurrentRoom: () => Room | undefined;
  getCurrentHistory: () => SpaceHistory | undefined;
};

// コンテキストの作成
const AppContext = createContext<AppContextType | undefined>(undefined);

// プロバイダーコンポーネント
/**
 * アプリケーションの状態を管理するプロバイダーコンポーネント
 *
 * @param children - 子要素
 */
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const router = useRouter();
  const searchParams = useSearchParams();

  // ルーム切り替え
  const switchRoom = useCallback(
    (roomId: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_RAD_LOADING', payload: false });

      // 現在の同期状態を保存
      const currentSyncEnabled = state.isSyncEnabled;
      console.log('[SWITCH_ROOM] Current sync state before room change:', currentSyncEnabled);

      // ルーム変更を先に適用
      dispatch({ type: 'SET_CURRENT_ROOM', payload: roomId });

      // 新しいルームの最新の空間履歴を選択
      const room = state.rooms.find((r) => r.id === roomId);
      if (room && room.histories.length > 0) {
        // 日付順にソートして最新の空間履歴を選択（降順 - 新しい順）
        const sortedHistories = [...room.histories].sort((a, b) => {
          // 日付が空の場合は最も古い日付として扱う
          if (!a.date) return 1;
          if (!b.date) return -1;

          // 日付文字列をDateオブジェクトに変換して比較
          const dateA = new Date(a.date.replace(/\//g, '-'));
          const dateB = new Date(b.date.replace(/\//g, '-'));

          return dateB.getTime() - dateA.getTime();
        });

        // ソート後の配列の最初の要素（最新の履歴）を選択
        const latestHistory = sortedHistories[0];
        if (latestHistory) {
          dispatch({ type: 'SET_CURRENT_HISTORY', payload: latestHistory.id });
          // ローディング状態はSparkViewerコンポーネントによって制御されるため、
          // ここでは何もしない
        }
      }

      // ルームに履歴がない場合はローディング状態をfalseに設定
      if (!room || room.histories.length === 0) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }

      // URLの更新（ルーム変更後に行う）
      const params = new URLSearchParams(searchParams.toString());
      params.set('room', roomId);

      // 現在のパスを取得
      const path = window.location.pathname;

      // URLの更新を非同期で行い、同期状態を確実に保持する
      setTimeout(() => {
        // 同期状態を確認して、変わっていたら元に戻す
        if (state.isSyncEnabled !== currentSyncEnabled) {
          console.log('[SWITCH_ROOM] Sync state changed during room change, restoring to:', currentSyncEnabled);
          dispatch({ type: 'SET_SYNC_ENABLED', payload: currentSyncEnabled });
        }

        // URLを更新（履歴APIを直接使用）
        try {
          window.history.replaceState({}, '', `${path}?${params.toString()}`);
          console.log('[SWITCH_ROOM] URL updated using history API');
        } catch {
          // 履歴APIが使用できない場合はNext.jsのルーターを使用
          console.log('[SWITCH_ROOM] History API failed, using Next.js router');
          router.replace(`${path}?${params.toString()}`);
        }
      }, 0);
    },
    [router, searchParams, state.rooms, state.isSyncEnabled]
  );

  // 空間履歴切り替え
  const switchHistory = useCallback((historyId: string) => {
    dispatch({ type: 'SET_CURRENT_HISTORY', payload: historyId });
  }, []);

  // 同期切り替え
  const toggleSync = useCallback(() => {
    dispatch({
      type: 'SET_SYNC_ENABLED',
      payload: !state.isSyncEnabled
    });
    console.log('[TOGGLE_FUNC] toggleSync called, dispatching SET_SYNC_ENABLED');
  }, [state.isSyncEnabled]);

  // 強制同期オン
  const forceSyncEnabled = useCallback(() => {
    dispatch({ type: 'FORCE_SYNC_ENABLED' });
    console.log('[FORCE_SYNC] forceSyncEnabled called, dispatching FORCE_SYNC_ENABLED');
  }, []);

  // 指差しポインター切り替え
  const togglePointer = useCallback(() => {
    dispatch({
      type: 'SET_POINTER_ENABLED',
      payload: !state.isPointerEnabled
    });
    console.log('[TOGGLE_FUNC] togglePointer called, dispatching SET_POINTER_ENABLED');
  }, [state.isPointerEnabled]);

  // UI表示/非表示切り替え
  const toggleUiHidden = useCallback(() => {
    dispatch({ type: 'TOGGLE_UI_HIDDEN' });
    console.log('[TOGGLE_FUNC] toggleUiHidden called, dispatching TOGGLE_UI_HIDDEN');
  }, []);

  // 現在のルーム取得
  const getCurrentRoom = useCallback(() => {
    return state.rooms.find((room) => room.id === state.currentRoomId);
  }, [state.rooms, state.currentRoomId]);

  // 現在の空間履歴取得
  const getCurrentHistory = useCallback(() => {
    const currentRoom = getCurrentRoom();
    if (!currentRoom) return undefined;

    return currentRoom.histories.find((history) => history.id === state.currentHistoryId);
  }, [getCurrentRoom, state.currentHistoryId]);

  // トースト表示
  const showToast = useCallback(
    (message: string) => {
      dispatch({ type: 'SHOW_TOAST', payload: message });
    },
    [dispatch]
  );

  // トースト非表示
  const hideToast = useCallback(() => {
    dispatch({ type: 'HIDE_TOAST' });
  }, [dispatch]);

  const value = {
    state,
    dispatch,
    switchRoom,
    switchHistory,
    toggleSync,
    forceSyncEnabled,
    togglePointer,
    toggleUiHidden,
    showToast,
    hideToast,
    getCurrentRoom,
    getCurrentHistory
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// カスタムフック
/**
 * アプリケーションのコンテキストを使用するためのカスタムフック
 *
 * @returns アプリケーションのコンテキスト
 * @throws AppProviderの外部で使用された場合にエラーをスロー
 */
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
